import React, { useState } from 'react';
import { 
  HelpCircle, ChevronDown, ChevronUp, MessageCircle, Mail, Search, 
  Image, Video, Music, FileText, Box, DollarSign,
  BookOpen, Handshake, Calendar, BarChart2, Rocket, Palette, Zap, Star, Camera,
  Wrench, Lightbulb, Library
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ChatWindow from './ChatWindow';
import AnimatedIcon from './ui/AnimatedIcon';

const HelpView: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'faq' | 'guide'>('faq');
  const [showChat, setShowChat] = useState(false);
  const { select } = useLanguage();

  const faqs = React.useMemo(
    () => [
      {
        question: select({
          es: '¿Cómo puedo crear mi primera publicación?',
          en: 'How can I create my first post?'
        }),
        answer: select({
          es: "Para crear tu primera publicación, ve a la sección 'Crear' en el menú principal. Puedes subir imágenes, videos, audio o documentos. Añade un título descriptivo, selecciona la categoría de arte correspondiente y escribe una descripción detallada de tu obra.",
          en: "To publish your first post, go to the 'Create' section in the main menu. Upload images, videos, audio, or documents, add a descriptive title, choose the right art category, and write a detailed description of your piece."
        })
      },
      {
        question: select({
          es: '¿Qué tipos de archivos puedo subir?',
          en: 'What file types can I upload?'
        }),
        answer: select({
          es: 'Puedes subir imágenes (JPG, PNG, GIF), videos (MP4, MOV, AVI), archivos de audio, documentos PDF, libros EPUB y modelos 3D. El tamaño máximo por archivo es de 10MB.',
          en: 'You can upload images (JPG, PNG, GIF), videos (MP4, MOV, AVI), audio files, PDF documents, EPUB books, and 3D models. The maximum file size per upload is 10MB.'
        })
      },
      {
        question: select({
          es: '¿Cómo puedo vender mi arte?',
          en: 'How can I sell my art?'
        }),
        answer: select({
          es: "Al crear una publicación, marca la casilla 'Esta obra está en venta' y establece un precio. Los compradores podrán contactarte directamente o comprar a través de la plataforma. SoArtistic cobra una comisión del 10% por venta.",
          en: "When creating a post, tick 'This piece is for sale' and set a price. Buyers can contact you directly or purchase through the platform. SoArtistic charges a 10% commission per sale."
        })
      },
      {
        question: select({
          es: '¿Cuál es la diferencia entre cuenta de artista y visitante?',
          en: 'What is the difference between an artist and a visitor account?'
        }),
        answer: select({
          es: 'Una cuenta de artista te permite publicar tus obras, vender arte, acceder a analíticas avanzadas y gestionar colaboraciones. Una cuenta de visitante te permite explorar, guardar arte favorito y contactar artistas.',
          en: 'An artist account lets you publish your work, sell art, access advanced analytics, and manage collaborations. A visitor account lets you explore, save favorites, and contact artists.'
        })
      },
      {
        question: select({
          es: '¿Cómo funciona el sistema de colaboraciones?',
          en: 'How does the collaboration system work?'
        }),
        answer: select({
          es: 'Los artistas pueden crear propuestas de colaboración especificando el tipo de proyecto, presupuesto y requisitos. Otros artistas pueden aplicar enviando un mensaje. Es una forma de conectar y trabajar juntos en proyectos creativos.',
          en: 'Artists can create collaboration proposals specifying the project type, budget, and requirements. Other artists can apply by sending a message. It is a way to connect and work together on creative projects.'
        })
      },
      {
        question: select({
          es: '¿Qué son las stories y cómo las creo?',
          en: 'What are stories and how do I create them?'
        }),
        answer: select({
          es: 'Las stories son contenido temporal que desaparece después de 24 horas. Puedes crear stories de texto, imagen o video. Son perfectas para mostrar tu proceso creativo, obras en progreso o momentos del día a día.',
          en: 'Stories are temporary content that disappears after 24 hours. Create text, image, or video stories—perfect for sharing your creative process, works in progress, or daily moments.'
        })
      },
      {
        question: select({
          es: '¿Cómo puedo ver las analíticas de mi perfil?',
          en: 'How can I view my profile analytics?'
        }),
        answer: select({
          es: "Ve a la sección 'Analíticas' en el menú principal. Ahí verás estadísticas detalladas sobre visualizaciones, likes, comentarios, crecimiento de seguidores y rendimiento de tus publicaciones.",
          en: "Go to the 'Analytics' section in the main menu. You will find detailed stats on views, likes, comments, follower growth, and the performance of your posts."
        })
      },
      {
        question: select({
          es: '¿Puedo cambiar el tema y apariencia de la aplicación?',
          en: 'Can I change the app theme and appearance?'
        }),
        answer: select({
          es: 'Sí, ve a Configuración > Apariencia. Puedes cambiar entre tema claro/oscuro, personalizar el color principal, cambiar la fuente y ajustar el tamaño del texto.',
          en: 'Yes, head to Settings > Appearance. Switch between light and dark themes, customize the primary color, change fonts, and adjust text size.'
        })
      },
      {
        question: select({
          es: '¿Cómo reporto contenido inapropiado?',
          en: 'How do I report inappropriate content?'
        }),
        answer: select({
          es: 'En cada publicación hay un menú de opciones (tres puntos) donde puedes reportar contenido. También puedes contactar directamente al equipo de soporte a través del chat de ayuda.',
          en: 'Each post has an options menu (three dots) where you can report content. You can also contact the support team directly through the help chat.'
        })
      },
      {
        question: select({
          es: '¿Mis datos personales están seguros?',
          en: 'Is my personal data safe?'
        }),
        answer: select({
          es: 'Sí, cumplimos con las regulaciones de protección de datos. Tu información está encriptada y nunca la compartimos con terceros sin tu consentimiento explícito.',
          en: 'Yes, we comply with data protection regulations. Your information is encrypted and never shared with third parties without your explicit consent.'
        })
      }
    ],
    [select]
  );

  const postTypes = React.useMemo(
    () => [
      {
        icon: Image,
        title: select({ es: 'Imágenes', en: 'Images' }),
        desc: select({
          es: 'Pinturas, fotografías, diseños. Formatos: JPG, PNG, GIF',
          en: 'Paintings, photos, designs. Formats: JPG, PNG, GIF'
        })
      },
      {
        icon: Video,
        title: select({ es: 'Videos', en: 'Videos' }),
        desc: select({
          es: 'Procesos creativos, timelapses. Formatos: MP4, MOV',
          en: 'Creative process, timelapses. Formats: MP4, MOV'
        })
      },
      {
        icon: Music,
        title: select({ es: 'Audio', en: 'Audio' }),
        desc: select({
          es: 'Música, podcasts. Formatos: MP3, WAV, FLAC + portada',
          en: 'Music, podcasts. Formats: MP3, WAV, FLAC + cover art'
        })
      },
      {
        icon: FileText,
        title: select({ es: 'Documentos', en: 'Documents' }),
        desc: select({
          es: 'Libros, portfolios. Formatos: PDF, EPUB + portada',
          en: 'Books, portfolios. Formats: PDF, EPUB + cover art'
        })
      },
      {
        icon: Box,
        title: select({ es: 'Modelos 3D', en: '3D models' }),
        desc: select({
          es: 'Esculturas digitales. Formatos: OBJ, FBX, GLTF',
          en: 'Digital sculptures. Formats: OBJ, FBX, GLTF'
        })
      },
      {
        icon: DollarSign,
        title: select({ es: 'Arte en venta', en: 'Art for sale' }),
        desc: select({
          es: 'Marca "En venta" y establece precio en euros',
          en: 'Mark “For sale” and set a price in euros'
        })
      }
    ],
    [select]
  );

  const featureCards = React.useMemo(
    () => [
      {
        icon: BookOpen,
        title: select({ es: 'Historias', en: 'Stories' }),
        description: select({ es: 'Comparte momentos temporales que desaparecen en 24h.', en: 'Share temporary moments that disappear after 24h.' }),
        tip: select({
          es: '💡 Perfecto para: Procesos creativos, obras en progreso, behind the scenes',
          en: '💡 Perfect for: Creative process, works in progress, behind the scenes'
        })
      },
      {
        icon: Handshake,
        title: select({ es: 'Colaboraciones', en: 'Collaborations' }),
        description: select({ es: 'Propone proyectos colaborativos con otros artistas.', en: 'Pitch collaborative projects with other artists.' }),
        tip: select({
          es: '💡 Especifica: Presupuesto, fecha límite, requisitos, ubicación',
          en: '💡 Specify: Budget, deadline, requirements, location'
        })
      },
      {
        icon: Calendar,
        title: select({ es: 'Eventos', en: 'Events' }),
        description: select({ es: 'Organiza exposiciones, talleres, conciertos.', en: 'Host exhibitions, workshops, concerts.' }),
        tip: select({
          es: '💡 Puede ser: Presencial, online, gratuito o de pago',
          en: '💡 Can be: In-person, online, free or paid'
        })
      },
      {
        icon: BarChart2,
        title: select({ es: 'Analíticas', en: 'Analytics' }),
        description: select({ es: 'Ve estadísticas de tu arte y crecimiento.', en: 'See stats about your art and growth.' }),
        tip: select({
          es: '💡 Incluye: Vistas, likes, seguidores, engagement',
          en: '💡 Includes: Views, likes, followers, engagement'
        })
      }
    ],
    [select]
  );

  const photoTips = React.useMemo(
    () => [
      select({ es: '• Resolución alta (mínimo 1080px)', en: '• High resolution (minimum 1080px)' }),
      select({ es: '• Buena iluminación', en: '• Good lighting' }),
      select({ es: '• Colores vibrantes', en: '• Vibrant colors' }),
      select({ es: '• Composición atractiva', en: '• Attractive composition' })
    ],
    [select]
  );

  const audioTips = React.useMemo(
    () => [
      select({ es: '• Calidad mínimo 192kbps', en: '• Minimum quality 192kbps' }),
      select({ es: '• Sube portada atractiva', en: '• Upload appealing cover art' }),
      select({ es: '• Título descriptivo', en: '• Descriptive title' }),
      select({ es: '• Tags relevantes', en: '• Relevant tags' })
    ],
    [select]
  );

  const monetizationSteps = React.useMemo(
    () => [
      select({ es: '1. Al crear post, marca "Esta obra está en venta"', en: '1. When creating a post, check “This piece is for sale”' }),
      select({ es: '2. Establece precio en euros (€)', en: '2. Set the price in euros (€)' }),
      select({ es: '3. Los compradores verán botón "Comprar"', en: '3. Buyers will see the "Buy" button' }),
      select({ es: '4. Recibes notificación de compra', en: '4. You receive a purchase notification' }),
      select({ es: '5. Gestiona pedido desde "Ventas"', en: '5. Manage the order from "Sales"' })
    ],
    [select]
  );

  const troubleshootingItems = React.useMemo(
    () => [
      {
        q: select({ es: 'No puedo subir archivos', en: 'I cannot upload files' }),
        a: select({ es: 'Verifica que el archivo no supere 10MB y esté en formato compatible', en: 'Make sure the file is under 10MB and uses a supported format' })
      },
      {
        q: select({ es: 'Mi post no aparece', en: 'My post is not showing up' }),
        a: select({ es: 'Los posts pueden tardar unos minutos en aparecer. Verifica que esté publicado', en: 'Posts can take a few minutes to appear. Check that it is published' })
      },
      {
        q: select({ es: 'No recibo notificaciones', en: 'I am not receiving notifications' }),
        a: select({ es: 'Ve a Configuración → Notificaciones y activa las que desees', en: 'Go to Settings → Notifications and enable the ones you need' })
      },
      {
        q: select({ es: 'Problemas de pagos', en: 'Payment issues' }),
        a: select({ es: 'Contacta soporte con detalles del error específico', en: 'Contact support with the details of the issue' })
      },
      {
        q: select({ es: 'Video no se reproduce', en: 'Video does not play' }),
        a: select({ es: 'Usa formato MP4 para mejor compatibilidad', en: 'Use MP4 format for the best compatibility' })
      }
    ],
    [select]
  );

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
          <AnimatedIcon icon={HelpCircle} className="text-purple-600" size={32} animation="rotate" />
          {select({ es: 'Centro de ayuda', en: 'Help center' })}
        </h1>
        <p className="text-gray-600 text-lg">
          {select({ es: 'Encuentra respuestas a las preguntas más frecuentes', en: 'Find answers to the most common questions' })}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'faq'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {select({ es: 'Preguntas frecuentes', en: 'FAQ' })}
          </button>
          <button
            onClick={() => setActiveTab('guide')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'guide'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {select({ es: 'Guía completa', en: 'Complete guide' })}
          </button>
        </div>
      </div>

      {/* FAQ Section */}
      {activeTab === 'faq' && (
        <>
          {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md mx-auto">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={select({ es: 'Buscar en preguntas frecuentes...', en: 'Search the frequently asked questions...' })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
            <AnimatedIcon icon={MessageCircle} className="text-purple-600" size={24} animation="bounce" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{select({ es: 'Chat en vivo', en: 'Live chat' })}</h3>
          <p className="text-gray-600 text-sm mb-4">
            {select({ es: 'Habla directamente con nuestro equipo de soporte', en: 'Talk directly with our support team' })}
          </p>
          <button 
            onClick={() => setShowChat(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {select({ es: 'Iniciar chat', en: 'Start chat' })}
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-md transition-shadow group">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
            <AnimatedIcon icon={Mail} className="text-blue-600" size={24} animation="bounce" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{select({ es: 'Email de soporte', en: 'Support email' })}</h3>
          <p className="text-gray-600 text-sm mb-4">
            {select({ es: 'Envíanos un email detallado con tu consulta', en: 'Send us a detailed email with your question' })}
          </p>
          <button 
            onClick={() => window.location.href = 'mailto:soporte@soartistic.com'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {select({ es: 'Enviar email', en: 'Send email' })}
          </button>
        </div>
      </div>

      {/* FAQs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{select({ es: 'Preguntas frecuentes', en: 'Frequently asked questions' })}</h2>
          <p className="text-gray-600 mt-1">
            {filteredFaqs.length}{' '}
            {filteredFaqs.length === 1
              ? select({ es: 'pregunta encontrada', en: 'question found' })
              : select({ es: 'preguntas encontradas', en: 'questions found' })}
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredFaqs.map((faq, index) => (
            <div key={index} className="p-6">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between text-left group"
              >
                <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                {openFaq === index ? (
                  <AnimatedIcon icon={ChevronUp} className="text-purple-600 flex-shrink-0" size={20} animation="scale" />
                ) : (
                  <AnimatedIcon icon={ChevronDown} className="text-gray-400 flex-shrink-0 group-hover:text-gray-600" size={20} animation="scale" />
                )}
              </button>
              
              {openFaq === index && (
                <div className="mt-4 text-gray-700 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredFaqs.length === 0 && (
          <div className="p-12 text-center">
            <AnimatedIcon icon={Search} size={48} className="mx-auto text-gray-300 mb-4" animation="pulse" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{select({ es: 'No se encontraron resultados', en: 'No results found' })}</h3>
            <p className="text-gray-500">{select({ es: 'Intenta con otros términos de búsqueda', en: 'Try different search terms' })}</p>
          </div>
        )}
      </div>
        </>
      )}

      {/* Complete Guide Section */}
      {activeTab === 'guide' && (
        <div className="space-y-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <AnimatedIcon icon={Library} className="text-purple-600" size={32} animation="bounce" />
              {select({ es: 'Guía completa de SoArtistic', en: 'SoArtistic complete guide' })}
            </h2>
            
            {/* Getting Started */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={Rocket} className="text-purple-600" size={24} animation="scale" />
                {select({ es: 'Primeros pasos', en: 'Getting started' })}
              </h3>
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">{select({ es: '1. Crear tu perfil', en: '1. Set up your profile' })}</h4>
                  <p className="text-purple-700 text-sm">{select({ es: 'Completa tu biografía, especialidades artísticas y sube una foto de perfil profesional.', en: 'Complete your bio, artistic specialties, and upload a professional profile photo.' })}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">{select({ es: '2. Tu primera publicación', en: '2. Your first post' })}</h4>
                  <p className="text-blue-700 text-sm">{select({ es: 'Ve a "Crear" → Selecciona categoría → Sube tu obra → Añade título y descripción.', en: 'Go to “Create” → Choose a category → Upload your work → Add a title and description.' })}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">{select({ es: '3. Conectar con la comunidad', en: '3. Connect with the community' })}</h4>
                  <p className="text-green-700 text-sm">{select({ es: 'Explora arte, sigue artistas, comenta y colabora en proyectos.', en: 'Explore art, follow artists, comment, and collaborate on projects.' })}</p>
                </div>
              </div>
            </div>

            {/* Types of Posts */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={Palette} className="text-purple-600" size={24} animation="rotate" />
                {select({ es: 'Tipos de publicaciones', en: 'Types of posts' })}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {postTypes.map((type, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 group hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-purple-600">
                        <AnimatedIcon icon={type.icon} size={28} animation="bounce" />
                      </span>
                      <h4 className="font-medium text-gray-900">{type.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600">{type.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Guide */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={Zap} className="text-yellow-500" size={24} animation="pulse" />
                {select({ es: 'Características principales', en: 'Key features' })}
              </h3>
              <div className="space-y-4">
                {featureCards.map((card, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 group hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <AnimatedIcon icon={card.icon} size={20} className="text-purple-600" animation="scale" />
                      <h4 className="font-medium text-gray-900">{card.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{card.description}</p>
                    <p className="text-xs text-gray-500">{card.tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Best Practices */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={Star} className="text-yellow-500" size={24} animation="rotate" />
                {select({ es: 'Mejores prácticas', en: 'Best practices' })}
              </h3>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                      <AnimatedIcon icon={Camera} size={20} animation="scale" />
                      {select({ es: 'Para fotografías/imágenes:', en: 'For photos/images:' })}
                    </h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      {photoTips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                      <AnimatedIcon icon={Music} size={20} animation="wiggle" />
                      {select({ es: 'Para audio/música:', en: 'For audio/music:' })}
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      {audioTips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Monetization */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={DollarSign} className="text-green-600" size={24} animation="bounce" />
                {select({ es: 'Monetización', en: 'Monetization' })}
              </h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h4 className="font-medium text-yellow-900 mb-3">{select({ es: 'Cómo vender tu arte:', en: 'How to sell your art:' })}</h4>
                <div className="space-y-2 text-sm text-yellow-800">
                  {monetizationSteps.map((step, index) => (
                    <p key={index}>{step}</p>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-white border border-yellow-200 rounded">
                  <p className="text-xs text-yellow-700">
                    <strong>{select({ es: 'Comisión:', en: 'Commission:' })}</strong>{' '}
                    {select({ es: 'SoArtistic cobra 10% (5% para usuarios Premium)', en: 'SoArtistic charges 10% (5% for Premium users)' })}
                  </p>
                </div>
              </div>
            </div>

            {/* Troubleshooting */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AnimatedIcon icon={Wrench} className="text-gray-600" size={24} animation="rotate" />
                {select({ es: 'Solución de problemas', en: 'Troubleshooting' })}
              </h3>
              <div className="space-y-3">
                {troubleshootingItems.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 group hover:bg-gray-100 transition-colors">
                    <h4 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                      <AnimatedIcon icon={HelpCircle} size={18} className="text-purple-600" animation="scale" />
                      {item.q}
                    </h4>
                    <p className="text-sm text-gray-600 flex items-start gap-2">
                      <AnimatedIcon icon={Lightbulb} size={16} className="text-yellow-500 mt-0.5 flex-shrink-0" animation="pulse" />
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Resources */}
      {activeTab === 'faq' && (
        <div className="mt-12 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{select({ es: '¿No encuentras lo que buscas?', en: 'Not finding what you need?' })}</h2>
          <p className="text-gray-600 mb-6">
            {select({ es: 'Nuestro equipo de soporte está aquí para ayudarte con cualquier pregunta específica', en: 'Our support team is here to help with any specific question' })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              {select({ es: 'Contactar soporte', en: 'Contact support' })}
            </button>
            <button 
              onClick={() => setActiveTab('guide')}
              className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
            >
              {select({ es: 'Ver guía completa', en: 'View complete guide' })}
            </button>
          </div>
        </div>
      </div>
      )}
      <ChatWindow isOpen={showChat} onClose={() => setShowChat(false)} />
    </div>
  );
};

export default HelpView;
